import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { IconPrinter, IconSend } from '@tabler/icons';
import DragDropArea from '../../referral/documentUploads/components/DragDropArea';
import ApiService from '../../common/services/ApiService';
import GlobalLoader from '../../common/components/loader/GlobalLoader';

const fieldSx = {
  '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root:hover, & .MuiOutlinedInput-root.Mui-focused': {
    borderRadius: 2,
    backgroundColor: '#ffffff !important',
    boxShadow: '0 1px 0 rgba(15, 23, 42, 0.03)',
  },
  '& .MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline': {
    backgroundColor: '#ffffff !important',
  },
};

const sectionSx = {
  border: '1px solid #dbe3ef',
  borderRadius: 2,
  p: 2,
  backgroundColor: '#f8fafc',
};

const sectionTitleSx = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#1e293b',
};

const formatFaxNumber = (value = '') => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function SendFaxForm({ onSend, saving = false }) {
  const [recipient, setRecipient] = useState('');
  const [faxNumber, setFaxNumber] = useState('');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    return () => {
      attachments.forEach(item => {
        if (item?.url) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, [attachments]);

  const createAttachmentItem = (file) => ({
    id: `${Date.now()}-${Math.random()}`,
    name: 'misc',
    displayName: file.name,
    file,
    url: URL.createObjectURL(file),
    status: 'done',
    progress: 100,
  });

  const handleFilesAdded = (files) => {
    const mapped = files.map(createAttachmentItem);
    setAttachments(prev => [...prev, ...mapped]);
    setErrors(prev => ({ ...prev, attachments: '' }));
  };

  const handleFileDelete = (fileObj) => {
    setAttachments(prev => {
      const target = prev.find(item => item.id === fileObj.id);
      if (target?.url) {
        URL.revokeObjectURL(target.url);
      }

      return prev.filter(item => item.id !== fileObj.id);
    });
  };

  const handleFileRename = (fileObj, nextName) => {
    setAttachments(prev => prev.map(item => {
      if (item.id !== fileObj.id) {
        return item;
      }

      return {
        ...item,
        displayName: nextName,
      };
    }));
  };

  const clearAttachments = () => {
    attachments.forEach(item => {
      if (item?.url) {
        URL.revokeObjectURL(item.url);
      }
    });
    setAttachments([]);
  };

  const handleAttachmentProgress = (attachmentId, patch = {}) => {
    setAttachments(prev => prev.map(item => (
      item.id === attachmentId
        ? { ...item, ...patch }
        : item
    )));
  };

  const handlePrintPreview = async () => {
    try {
      setSubmitError('');
      setPreviewing(true);
      await new Promise(resolve => setTimeout(resolve, 0));

      const formData = new FormData();
      formData.append('recipient', recipient.trim());
      formData.append('fax_number', faxNumber);
      formData.append('message', message);
      formData.append('details', details);
      attachments.forEach((item) => {
        if (item?.file) {
          formData.append('files[]', item.file);
        }
      });

      const response = await ApiService.previewSentFax(formData);
      const url = response?.url;

      if (!url) {
        setSubmitError('Unable to generate print preview right now.');
        return;
      }

      const previewResponse = await ApiService.getPDFFileAsBlob(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(previewResponse.data);
      const previewWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

      if (!previewWindow) {
        URL.revokeObjectURL(blobUrl);
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (err) {
      setSubmitError(err?.data?.message || err?.message || 'Unable to generate print preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (saving || previewing || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    const faxDigits = faxNumber.replace(/\D/g, '');
    const nextErrors = {};

    if (!recipient.trim()) {
      nextErrors.recipient = 'Fax To is required.';
    }

    if (faxDigits.length !== 10) {
      nextErrors.faxNumber = 'Enter a valid 10-digit fax number.';
    }

    if (!message.trim()) {
      nextErrors.message = 'Message is required.';
    }

    if (attachments.length < 1) {
      nextErrors.attachments = 'At least 1 attachment is required.';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      setSubmitError('');

      await onSend({
        recipient: recipient.trim(),
        faxNumber,
        patient: '',
        status: 'Queued',
        message,
        details,
        attachments,
        onAttachmentProgress: handleAttachmentProgress,
      });

      setRecipient('');
      setFaxNumber('');
      setMessage('');
      setDetails('');
      clearAttachments();
      setErrors({});
    } catch (err) {
      setSubmitError(err?.message || 'Unable to save fax. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#ffffff' }}>
      {previewing ? <GlobalLoader label="Generating print preview..." /> : null}
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Send Fax
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and send a patient referral fax.
          </Typography>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ mb: 2.25, pb: 1.25, borderBottom: '1px solid #cfd8e5' }}>
            <Typography sx={sectionTitleSx}>
              Recipient Information
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Fax To
              </Typography>
              <TextField
                placeholder="Enter clinic or recipient name"
                value={recipient}
                onChange={e => {
                  setRecipient(e.target.value);
                  if (errors.recipient) {
                    setErrors(prev => ({ ...prev, recipient: '' }));
                  }
                }}
                sx={fieldSx}
                error={Boolean(errors.recipient)}
                helperText={errors.recipient || ''}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Fax Number
              </Typography>
              <TextField
                placeholder="Enter 10 Digit Fax Number"
                value={faxNumber}
                onChange={e => {
                  setFaxNumber(formatFaxNumber(e.target.value));
                  if (errors.faxNumber) {
                    setErrors(prev => ({ ...prev, faxNumber: '' }));
                  }
                }}
                sx={fieldSx}
                inputProps={{ inputMode: 'numeric', maxLength: 14 }}
                error={Boolean(errors.faxNumber)}
                helperText={errors.faxNumber || ''}
                fullWidth
              />
            </Box>
          </Stack>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ mb: 2.25, pb: 1.25, borderBottom: '1px solid #cfd8e5' }}>
            <Typography sx={sectionTitleSx}>
              Fax Details
            </Typography>
          </Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Message (The receiver will see this on the cover page)
              </Typography>
              <TextField
                placeholder="Enter your message"
                value={message}
                onChange={e => {
                  setMessage(e.target.value);
                  if (errors.message) {
                    setErrors(prev => ({ ...prev, message: '' }));
                  }
                }}
                multiline
                minRows={3}
                sx={fieldSx}
                error={Boolean(errors.message)}
                helperText={errors.message || ''}
                fullWidth
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Details (Internal Notes)
              </Typography>
              <TextField
                placeholder="Enter additional fax details"
                value={details}
                onChange={e => setDetails(e.target.value)}
                multiline
                minRows={4}
                sx={fieldSx}
                fullWidth
              />
            </Box>
            
            <DragDropArea
              key="send-fax-attachments"
              label="Attachments"
              files={attachments}
              onFilesAdded={handleFilesAdded}
              onFileDelete={handleFileDelete}
              onFileRename={handleFileRename}
              cardSx={{
                backgroundColor: '#ffffff',
                '& .MuiCardContent-root': {
                  backgroundColor: '#ffffff',
                },
              }}
            />
            {errors.attachments ? (
              <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                {errors.attachments}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<IconPrinter size={16} />}
            onClick={handlePrintPreview}
            disabled={saving || previewing || submitting}
          >
            {previewing ? 'Generating Preview...' : 'Print Preview'}
          </Button>
          <Button variant="contained" startIcon={<IconSend size={16} />} onClick={handleSend} disabled={saving || submitting}>
            Send Fax
          </Button>
        </Stack>
        {submitError ? (
          <Typography variant="caption" color="error" sx={{ textAlign: 'right' }}>
            {submitError}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}
